import React, { useEffect, useMemo, useRef, useState } from "react";
import { CompetenceItem } from "../../../models/Competence";
import { CompetenceSelection } from "../../../models/ConsultantFilter";
import { cn } from "../../../utils/cn";

/**
 * A tri-state checkbox tree for selecting one or many competences across the
 * hierarchy branch (industry) → service → skill.
 *
 * Each node shows the familiar `[ ] [-] [x]` convention:
 *   - unchecked: none of its leaves selected
 *   - partial:   some of its leaves selected
 *   - checked:   all of its leaves selected
 *
 * Selection is modelled on the smallest meaningful unit ("leaf"): a skill when
 * the service has skills, otherwise the service itself. The component converts
 * to/from the compact CompetenceSelection[] shape the filter stores, collapsing
 * fully-selected services to `{branch, service}` and fully-selected branches to
 * `{branch}`.
 */

type Leaf = { branch: string; service: string; skill?: string };

const SEP = " ";
const leafKey = (l: Leaf) => [l.branch, l.service, l.skill ?? ""].join(SEP);
const label = (item: CompetenceItem) => item.name?.sv || item.key;
const hasSkills = (service: CompetenceItem) => !!service.items?.length;

function leavesUnderService(branch: string, service: CompetenceItem): Leaf[] {
	if (hasSkills(service))
		return service.items!.map((skill) => ({ branch, service: service.key, skill: skill.key }));
	return [{ branch, service: service.key }];
}

function leavesUnderBranch(branch: CompetenceItem): Leaf[] {
	return (branch.items ?? []).flatMap((service) => leavesUnderService(branch.key, service));
}

/** Expand the stored selections into the set of selected leaf keys for rendering. */
function selectionsToLeafKeys(
	tree: CompetenceItem[],
	selections: CompetenceSelection[]
): Set<string> {
	const set = new Set<string>();

	for (const sel of selections) {
		const branch = tree.find((b) => b.key === sel.branch);
		if (!branch) continue;

		if (!sel.service) {
			leavesUnderBranch(branch).forEach((l) => set.add(leafKey(l)));
			continue;
		}

		const service = (branch.items ?? []).find((s) => s.key === sel.service);
		if (!service) {
			set.add(leafKey({ branch: sel.branch, service: sel.service }));
			continue;
		}

		if (sel.skills?.length && hasSkills(service)) {
			sel.skills.forEach((skill) =>
				set.add(leafKey({ branch: sel.branch, service: sel.service!, skill }))
			);
		} else {
			leavesUnderService(sel.branch, service).forEach((l) => set.add(leafKey(l)));
		}
	}

	return set;
}

/** Collapse the selected leaf keys back into the compact CompetenceSelection[]. */
function leafKeysToSelections(
	tree: CompetenceItem[],
	selected: Set<string>
): CompetenceSelection[] {
	const out: CompetenceSelection[] = [];

	for (const branch of tree) {
		const branchLeaves = leavesUnderBranch(branch);
		if (!branchLeaves.length) continue;

		const selectedBranch = branchLeaves.filter((l) => selected.has(leafKey(l)));
		if (!selectedBranch.length) continue;

		if (selectedBranch.length === branchLeaves.length) {
			out.push({ branch: branch.key });
			continue;
		}

		for (const service of branch.items ?? []) {
			const serviceLeaves = leavesUnderService(branch.key, service);
			const selectedService = serviceLeaves.filter((l) => selected.has(leafKey(l)));
			if (!selectedService.length) continue;

			// Skill-less service: its single leaf is the service itself.
			if (!hasSkills(service)) {
				out.push({ branch: branch.key, service: service.key });
				continue;
			}

			// Keep the explicit skill list even when all are selected, so "All"
			// (AND) matching stays exact at the skill level.
			out.push({
				branch: branch.key,
				service: service.key,
				skills: selectedService.map((l) => l.skill!).filter(Boolean),
			});
		}
	}

	return out;
}

type NodeState = "checked" | "partial" | "unchecked";

function nodeState(leaves: Leaf[], selected: Set<string>): NodeState {
	const n = leaves.filter((l) => selected.has(leafKey(l))).length;
	if (n === 0) return "unchecked";
	if (n === leaves.length) return "checked";
	return "partial";
}

/**
 * Native checkbox so it inherits the exact same accent colour as the rest of
 * the app's checkboxes. The `[-]` (partial) state uses the native indeterminate
 * rendering. It's display-only — the surrounding row handles clicks.
 */
const TriStateCheckbox: React.FC<{ state: NodeState }> = ({ state }) => {
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (ref.current) ref.current.indeterminate = state === "partial";
	}, [state]);

	return (
		<input
			ref={ref}
			type="checkbox"
			checked={state === "checked"}
			readOnly
			className="pointer-events-none h-4 w-4 shrink-0"
		/>
	);
};

const Chevron: React.FC<{
	open: boolean;
	onClick: (e: React.MouseEvent) => void;
	hidden?: boolean;
}> = ({ open, onClick, hidden }) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(
			"flex h-4 w-4 shrink-0 items-center justify-center text-gray-500 transition-transform",
			hidden && "invisible",
			open && "rotate-90"
		)}
		aria-hidden={hidden}
	>
		▸
	</button>
);

export interface CompetenceCheckboxTreeProps {
	/** the org competence tree (branch → service → skills) */
	competences: CompetenceItem[];
	/** currently-selected competence criteria */
	value: CompetenceSelection[];
	onChange: (selections: CompetenceSelection[]) => void;
	/** show the child count next to branches/services. Defaults to true. */
	showCounts?: boolean;
}

/**
 * A single tree row. Defined at module scope (not inside the component) so its
 * identity is stable across renders — otherwise React remounts the whole tree
 * on every expand/toggle, which resets the scroll position.
 */
const Row: React.FC<{
	level: number;
	state: NodeState;
	text: string;
	count?: number;
	onToggle: () => void;
	chevron?: { open: boolean; onClick: () => void };
}> = ({ level, state, text, count, onToggle, chevron }) => (
	<div
		className="flex cursor-pointer select-none items-center gap-2 py-1 hover:bg-gray-50"
		style={{ paddingLeft: level * 18 }}
		onClick={onToggle}
	>
		{chevron ? (
			<Chevron
				open={chevron.open}
				onClick={(e) => {
					e.stopPropagation();
					chevron.onClick();
				}}
			/>
		) : (
			<span className="h-4 w-4 shrink-0" />
		)}
		<TriStateCheckbox state={state} />
		<span className="text-base text-gray-700">
			{text}
			{count ? <span className="ml-1 text-xs text-gray-400">({count})</span> : null}
		</span>
	</div>
);

const CompetenceCheckboxTree: React.FC<CompetenceCheckboxTreeProps> = ({
	competences,
	value,
	onChange,
	showCounts = true,
}) => {
	const selected = useMemo(() => selectionsToLeafKeys(competences, value), [competences, value]);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const toggleExpand = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});

	const setLeaves = (leaves: Leaf[], select: boolean) => {
		const next = new Set(selected);
		leaves.forEach((l) => (select ? next.add(leafKey(l)) : next.delete(leafKey(l))));
		onChange(leafKeysToSelections(competences, next));
	};

	return (
		<div className="flex flex-col">
			{competences.map((branch) => {
				const branchLeaves = leavesUnderBranch(branch);
				const branchState = nodeState(branchLeaves, selected);
				const branchOpen = expanded.has(branch.key);

				return (
					<React.Fragment key={branch.key}>
						<Row
							level={0}
							state={branchState}
							text={label(branch)}
							count={showCounts ? branch.items?.length : undefined}
							onToggle={() => setLeaves(branchLeaves, branchState !== "checked")}
							chevron={
								branch.items?.length
									? { open: branchOpen, onClick: () => toggleExpand(branch.key) }
									: undefined
							}
						/>

						{branchOpen &&
							(branch.items ?? []).map((service) => {
								const serviceLeaves = leavesUnderService(branch.key, service);
								const serviceState = nodeState(serviceLeaves, selected);
								const serviceId = `${branch.key}${SEP}${service.key}`;
								const serviceOpen = expanded.has(serviceId);

								return (
									<React.Fragment key={serviceId}>
										<Row
											level={1}
											state={serviceState}
											text={label(service)}
											count={showCounts && hasSkills(service) ? service.items!.length : undefined}
											onToggle={() => setLeaves(serviceLeaves, serviceState !== "checked")}
											chevron={
												hasSkills(service)
													? { open: serviceOpen, onClick: () => toggleExpand(serviceId) }
													: undefined
											}
										/>

										{serviceOpen &&
											hasSkills(service) &&
											service.items!.map((skill) => {
												const leaf: Leaf = {
													branch: branch.key,
													service: service.key,
													skill: skill.key,
												};
												const checked = selected.has(leafKey(leaf));

												return (
													<Row
														key={skill.key}
														level={2}
														state={checked ? "checked" : "unchecked"}
														text={label(skill)}
														onToggle={() => setLeaves([leaf], !checked)}
													/>
												);
											})}
									</React.Fragment>
								);
							})}
					</React.Fragment>
				);
			})}
		</div>
	);
};

export default CompetenceCheckboxTree;
